import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EntityId } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAppDispatch, useAppSelector } from "app/hooks";

import { Dialog, DialogTitle } from "@mui/material";

import { RootState } from "app/store";
import { selectDevicesBySiteId } from "features/devices/store/selectors";
import { devicesThunks } from "features/devices/store/slice";
import { selectCaseManagerConfiguration } from "features/config/store/selectors";

import { useCreateDossierMutation } from "services/streamingApi/caseManager.api";

import { DossierDialog } from "./DossierDialog";
import {
    CreateDossierRequest,
    isCreateDossierRequest,
} from "types/CreateDossierRequest";
import { PatchDossierInformationRequest } from "types/PatchDossierInformationRequest";

export interface CreateDossierDialogProps {
    closeDialog: () => void;
}

export function CreateDossierDialog(props: CreateDossierDialogProps) {
    const dispatch = useAppDispatch();

    const caseManagerConfiguration = useAppSelector(
        selectCaseManagerConfiguration,
    );

    const [selectedSiteId, setSelectedSiteId] = useState<
        EntityId | undefined
    >();
    const availableDevices = useAppSelector((state: RootState) =>
        selectDevicesBySiteId(state, selectedSiteId ?? ""),
    );
    const [isLoadingAvailableDevices, setIsLoadingAvailableDevices] =
        useState<boolean>(false);
    const [errorLoadingAvailableDevices, setErrorLoadingAvailableDevices] =
        useState<object | undefined>();

    const navigate = useNavigate();

    const [
        createDossier,
        { isLoading: isSaving, isSuccess, isError, error, data },
    ] = useCreateDossierMutation();
    const [validationErrors, setValidationErrors] =
        useState<Record<string, string[]>>();
    const [genericError, setGenericError] = useState<object | undefined>(
        undefined,
    );

    useEffect(() => {
        if (isSuccess && data) {
            // redirect
            navigate("/cases/" + data.id);
        }
    }, [isSuccess, data]);

    useEffect(() => {
        setGenericError(undefined);
        setValidationErrors(undefined);

        if (!isError) return;

        const fetchBaseQueryError = error as FetchBaseQueryError;

        if (fetchBaseQueryError.status === 400 && fetchBaseQueryError.data) {
            const castError = fetchBaseQueryError.data as unknown as {
                errors: Record<string, string[]>;
            };
            setValidationErrors(castError.errors);
        } else {
            setGenericError(error);
        }
    }, [isError, error]);

    useEffect(() => {
        if (selectedSiteId) {
            setIsLoadingAvailableDevices(true);
            setErrorLoadingAvailableDevices(undefined);

            dispatch(devicesThunks.fetchDevicesForSiteAsync(selectedSiteId))
                .catch((reason: object) => {
                    setErrorLoadingAvailableDevices(reason);
                })
                .finally(() => {
                    setIsLoadingAvailableDevices(false);
                });
        }
    }, [selectedSiteId]);

    return (
        <Dialog
            open={true}
            fullWidth={true}
            maxWidth={"lg"}
            disableEscapeKeyDown
            onClose={(event: object, reason: string) => {
                if (reason !== "backdropClick") {
                    props.closeDialog();
                }
            }}
            slotProps={{
                paper: {
                    component: "form",
                    onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                        event.preventDefault();

                        props.closeDialog();
                    },
                },
            }}
        >
            <DialogTitle>Create dossier</DialogTitle>
            <DossierDialog
                onCancel={props.closeDialog}
                onSave={(
                    draftDossierRequest:
                        | CreateDossierRequest
                        | PatchDossierInformationRequest,
                ) =>
                    isCreateDossierRequest(draftDossierRequest) &&
                    createDossier(draftDossierRequest)
                }
                onSiteIdSelected={(siteId?: EntityId) => {
                    setSelectedSiteId(siteId);
                }}
                isSaving={isSaving}
                existingDossier={undefined}
                genericError={genericError}
                validationErrors={validationErrors}
                availableDevices={availableDevices}
                isLoadingAvailableDevices={isLoadingAvailableDevices}
                errorLoadingAvailableDevices={errorLoadingAvailableDevices}
                maxNumberOfDays={caseManagerConfiguration?.maxNumberOfDays ?? 0}
            />
        </Dialog>
    );
}
